import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Entity, EntityCategory, EntityPlacement } from '../types/EntityCatalogue'

export interface EntityCatalogueState {
    categories: EntityCategory[]
    entities: Entity[]
    placements: EntityPlacement[]
    selectedCategoryId: string | null
    selectedEntityId: string | null
    isPlacementMode: boolean
    currentPlacement: Partial<EntityPlacement> | null
    isLoading: boolean
    error: string | null
}

const initialState: EntityCatalogueState = {
    categories: [],
    entities: [],
    placements: [],
    selectedCategoryId: null,
    selectedEntityId: null,
    isPlacementMode: false,
    currentPlacement: null,
    isLoading: false,
    error: null
}

export const entityCatalogueSlice = createSlice({
    name: 'entityCatalogue',
    initialState,
    reducers: {
        // Category actions
        setCategories: (state, action: PayloadAction<EntityCategory[]>) => {
            state.categories = action.payload
        },
        addCategory: (state, action: PayloadAction<EntityCategory>) => {
            state.categories.push(action.payload)
        },
        updateCategory: (state, action: PayloadAction<EntityCategory>) => {
            const index = state.categories.findIndex(c => c.id === action.payload.id)
            if (index !== -1) {
                state.categories[index] = action.payload
            }
        },
        deleteCategory: (state, action: PayloadAction<string>) => {
            state.categories = state.categories.filter(c => c.id !== action.payload)
        },
        setSelectedCategory: (state, action: PayloadAction<string | null>) => {
            state.selectedCategoryId = action.payload
            // Clear selected entity when changing category
            state.selectedEntityId = null
        },

        // Entity actions
        setEntities: (state, action: PayloadAction<Entity[]>) => {
            state.entities = action.payload
        },
        addEntity: (state, action: PayloadAction<Entity>) => {
            state.entities.push(action.payload)
        },
        updateEntity: (state, action: PayloadAction<Entity>) => {
            const index = state.entities.findIndex(e => e.id === action.payload.id)
            if (index !== -1) {
                state.entities[index] = action.payload
            }
        },
        deleteEntity: (state, action: PayloadAction<string>) => {
            state.entities = state.entities.filter(e => e.id !== action.payload)
        },
        setSelectedEntity: (state, action: PayloadAction<string | null>) => {
            state.selectedEntityId = action.payload
            // Enable placement mode when an entity is selected
            state.isPlacementMode = action.payload !== null

            // Create a current placement object if entity is selected
            if (action.payload) {
                const entity = state.entities.find(e => e.id === action.payload)
                if (entity) {
                    state.currentPlacement = {
                        entityId: entity.id,
                        x: 0,
                        y: 0,
                        rotation: 0,
                        scale: 1,
                        properties: {}
                    }
                }
            } else {
                state.currentPlacement = null
            }
        },

        // Placement actions
        setPlacements: (state, action: PayloadAction<EntityPlacement[]>) => {
            state.placements = action.payload
        },
        addPlacement: (state, action: PayloadAction<EntityPlacement>) => {
            state.placements.push(action.payload)
        },
        updatePlacement: (state, action: PayloadAction<EntityPlacement>) => {
            const index = state.placements.findIndex(p => p.id === action.payload.id)
            if (index !== -1) {
                state.placements[index] = action.payload
            }
        },
        deletePlacement: (state, action: PayloadAction<string>) => {
            state.placements = state.placements.filter(p => p.id !== action.payload)
        },
        updateCurrentPlacement: (state, action: PayloadAction<Partial<EntityPlacement>>) => {
            if (state.currentPlacement) {
                state.currentPlacement = {
                    ...state.currentPlacement,
                    ...action.payload
                }
            }
        },
        clearCurrentPlacement: (state) => {
            state.currentPlacement = null
            state.isPlacementMode = false
        },

        // UI state actions
        setPlacementMode: (state, action: PayloadAction<boolean>) => {
            state.isPlacementMode = action.payload
            if (!action.payload) {
                state.currentPlacement = null
            }
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload
        }
    }
})

export const {
    setCategories,
    addCategory,
    updateCategory,
    deleteCategory,
    setSelectedCategory,
    setEntities,
    addEntity,
    updateEntity,
    deleteEntity,
    setSelectedEntity,
    setPlacements,
    addPlacement,
    updatePlacement,
    deletePlacement,
    updateCurrentPlacement,
    clearCurrentPlacement,
    setPlacementMode,
    setLoading,
    setError
} = entityCatalogueSlice.actions

export default entityCatalogueSlice.reducer 